import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { EngagementLetter, RETAINER_TERMS_CONDITIONS } from '../types';
import { db, storage, ensureAuth } from '../utils/firebase';
import { collection, getDocs, doc, updateDoc, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../utils/AuthContext';
import { isPortalStaffUser } from '../utils/userRoles';
import { isDealWonNotifyRecipient } from '../utils/dealWonNotify';
import { getPublicSettings, AppSettings } from '../utils/appSettings';
import {
  verifyStakeholderTokenCallable,
  readStakeholderClaimsFromToken,
} from '../utils/stakeholderAuth';
import {
  getPortalAccessMeta,
  getEngagementLetterBySlug,
  signEngagementLetterServer,
} from '../utils/portalPinService';
import { isVoidedEngagementLetter } from '../utils/engagementLetterStatus';
import SignaturePad from '../components/engagement/SignaturePad';
import { ChevronDown, ChevronRight, Download, ArrowLeft } from 'lucide-react';
import jsPDF from 'jspdf';
import './EngagementPage.css';

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultOpen = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <section className={`content-section collapsible ${className} ${isOpen ? 'open' : ''}`}>
      <div
        className="collapsible-header"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        <h2 className="section-title">{title}</h2>
      </div>
      {isOpen && (
        <div className="collapsible-content">
          {children}
        </div>
      )}
    </section>
  );
};

const EngagementPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const portalSlug = searchParams.get('portalSlug');
  const cameFromPortal = searchParams.get('from') === 'portal' && !!portalSlug;
  const stakeholderTokenParam = searchParams.get('stakeholderToken');
  const [letter, setLetter] = useState<EngagementLetter | null>(null);
  const [letterId, setLetterId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentLinks, setPaymentLinks] = useState<{ id: string; name: string; url: string }[]>([]);

  // Docs settings state
  const [engagementLogo, setEngagementLogo] = useState<{ url: string; name: string } | null>(null);
  const [engagementBackground, setEngagementBackground] = useState<{ url: string; name: string } | null>(null);
  const [engagement3DModel, setEngagement3DModel] = useState<{ url: string; name: string } | null>(null);
  const [engagementVideoUrl, setEngagementVideoUrl] = useState('');
  const [retainerTotalFeeRange, setRetainerTotalFeeRange] = useState('');
  const [conceptPaymentLinkId, setConceptPaymentLinkId] = useState('');
  const [retainerPaymentLinkId, setRetainerPaymentLinkId] = useState('');
  const [samplePlansImage, setSamplePlansImage] = useState<{ url: string; name: string } | null>(null);
  const isStaffPortalUser = !!user && isPortalStaffUser(user.role);
  const [accessDenied, setAccessDenied] = useState(false);
  const [stakeholderDisplayName, setStakeholderDisplayName] = useState<string | null>(null);
  const isStakeholderViewer = !isStaffPortalUser && stakeholderDisplayName !== null;

  // Load settings (payment links and docs settings)
  useEffect(() => {
    const loadSettings = async () => {
      await ensureAuth();
      const settings = await getPublicSettings();

      // Payment links
      if (settings?.quickbooksPaymentLinks) {
        setPaymentLinks(settings.quickbooksPaymentLinks);
      } else if (settings?.quickbooksPaymentLink) {
        setPaymentLinks([{ id: 'default', name: 'Pay Now', url: settings.quickbooksPaymentLink }]);
      }

      // Docs settings
      if (settings?.engagementLogo) {
        setEngagementLogo(settings.engagementLogo);
      }
      if (settings?.engagementBackground) {
        setEngagementBackground(settings.engagementBackground);
      }
      if (settings?.engagement3DModel) {
        setEngagement3DModel(settings.engagement3DModel);
      }
      if (settings?.engagementVideoUrl) {
        setEngagementVideoUrl(settings.engagementVideoUrl);
      }
      if (settings?.retainerTotalFeeRange) {
        setRetainerTotalFeeRange(settings.retainerTotalFeeRange);
      }
      if (settings?.conceptPaymentLinkId) {
        setConceptPaymentLinkId(settings.conceptPaymentLinkId);
      }
      if (settings?.retainerPaymentLinkId) {
        setRetainerPaymentLinkId(settings.retainerPaymentLinkId);
      }
      if (settings?.samplePlansImage) {
        setSamplePlansImage(settings.samplePlansImage);
      }
    };
    loadSettings();
  }, []);

  // Fetch engagement letter
  useEffect(() => {
    const fetchLetter = async () => {
      if (!slug) {
        setError('Invalid engagement letter URL');
        setLoading(false);
        return;
      }

      try {
        await ensureAuth();

        // Access gating: employees; portal visitors; stakeholder token + verify
        // Guests cannot list engagementLetters — claim must exist before callable load.
        if (!isStaffPortalUser) {
          const sessionKey = `engagement_verified_${slug}`;
          let isVerified = !!sessionStorage.getItem(sessionKey);

          if (stakeholderTokenParam && portalSlug) {
            try {
              // Resolve portalId via callable meta — never slug-query clientPortals (PIN rules deny lists).
              const accessMeta = await getPortalAccessMeta(portalSlug);
              const res = await verifyStakeholderTokenCallable({
                token: stakeholderTokenParam,
                portalId: accessMeta.portalId,
              });
              setStakeholderDisplayName(res.displayName);
              sessionStorage.setItem(sessionKey, 'true');
              isVerified = true;
            } catch (e) {
              console.error('[Engagement] Stakeholder verify failed:', e);
              setAccessDenied(true);
              setLoading(false);
              return;
            }
          } else if (!stakeholderTokenParam) {
            const c = await readStakeholderClaimsFromToken();
            if (c.isStakeholder && c.displayName) {
              setStakeholderDisplayName(c.displayName);
              sessionStorage.setItem(sessionKey, 'true');
              isVerified = true;
            }
          }

          if (!isVerified && !cameFromPortal) {
            setAccessDenied(true);
            setLoading(false);
            return;
          }
          if (cameFromPortal && !sessionStorage.getItem(sessionKey)) {
            sessionStorage.setItem(sessionKey, 'true');
          }
        }

        // Callable resolves slug (guest list denied) and may mark viewed server-side.
        const payload = await getEngagementLetterBySlug(slug, { markViewed: true });
        await loadLetterData(payload.id, payload);
      } catch (err) {
        console.error('Error fetching engagement letter:', err);
        setError('Failed to load engagement letter');
      } finally {
        setLoading(false);
      }
    };

    fetchLetter();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isStaffPortalUser, cameFromPortal, stakeholderTokenParam, portalSlug]);

  const loadLetterData = async (id: string, data: any) => {
    const asDate = (v: unknown): Date | undefined => {
      if (!v) return undefined;
      if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
        return (v as { toDate: () => Date }).toDate();
      }
      const d = new Date(v as string | number | Date);
      return Number.isNaN(d.getTime()) ? undefined : d;
    };
    const letterData: EngagementLetter = {
      id,
      leadId: data.leadId,
      dealId: data.dealId,
      slug: data.slug,
      accessPin: data.accessPin,
      letterType: data.letterType,
      title: data.title,
      clientName: data.clientName,
      clientEmail: data.clientEmail,
      projectDescription: data.projectDescription,
      designFee: data.designFee,
      deliverables: data.deliverables || [],
      termsAndConditions: data.termsAndConditions,
      status: data.status,
      createdAt: asDate(data.createdAt) || new Date(),
      updatedAt: asDate(data.updatedAt) || new Date(),
      sentAt: asDate(data.sentAt),
      viewedAt: asDate(data.viewedAt),
      signedAt: asDate(data.signedAt),
      paidAt: asDate(data.paidAt),
      signature: data.signature
        ? {
            ...data.signature,
            signedAt: asDate(data.signature.signedAt) || data.signature.signedAt,
          }
        : data.signature,
      paymentId: data.paymentId,
      signedPdfUrl: data.signedPdfUrl,
    };

    setLetter(letterData);
    setLetterId(id);

    // Viewed status is recorded server-side by getEngagementLetterBySlug (guests cannot update).

    // Auto-generate and save PDF if employee views a signed letter without PDF
    if (isStaffPortalUser &&
      (letterData.status === 'signed' || letterData.status === 'paid') &&
      letterData.signature &&
      !letterData.signedPdfUrl) {
      try {
        // Parse signature date (handle Firestore Timestamp)
        const sigDate = letterData.signature.signedAt as any;
        const signedAtDate = sigDate?.toDate ? sigDate.toDate() : new Date(sigDate);

        const pdfBlob = await generateSignedPdf(
          letterData,
          letterData.signature.dataUrl,
          letterData.signature.signedBy,
          signedAtDate
        );

        const pdfFileName = `engagement-letters/${id}/${letterData.slug}-signed.pdf`;
        const storageRef = ref(storage, pdfFileName);
        await uploadBytes(storageRef, pdfBlob);
        const signedPdfUrl = await getDownloadURL(storageRef);

        await updateDoc(doc(db, 'engagementLetters', id), {
          signedPdfUrl,
          updatedAt: Timestamp.now(),
        });

        letterData.signedPdfUrl = signedPdfUrl;
        setLetter({ ...letterData });
        console.log('Auto-generated and saved signed PDF');
      } catch (pdfErr) {
        console.warn('Could not auto-generate PDF:', pdfErr);
      }
    }
  };

  // Generate signed PDF
  const generateSignedPdf = async (
    letterData: EngagementLetter,
    signatureDataUrl: string,
    signedBy: string,
    signedAt: Date
  ): Promise<Blob> => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = 20;

    // Helper to add text with word wrap
    const addText = (text: string, fontSize: number, isBold = false, color = '#111827') => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
      const rgb = parseInt(color.slice(1), 16);
      pdf.setTextColor((rgb >> 16) & 255, (rgb >> 8) & 255, rgb & 255);
      const lines = pdf.splitTextToSize(text, contentWidth);
      pdf.text(lines, margin, yPos);
      yPos += lines.length * (fontSize * 0.4) + 4;
    };

    // Check if we need a new page
    const checkNewPage = (neededSpace: number) => {
      if (yPos + neededSpace > pdf.internal.pageSize.getHeight() - 20) {
        pdf.addPage();
        yPos = 20;
      }
    };

    // Header with logo
    let headerHeight = 45;
    pdf.setFillColor(44, 62, 47);

    // Try to add logo
    if (engagementLogo?.url) {
      try {
        headerHeight = 55;
        pdf.rect(0, 0, pageWidth, headerHeight, 'F');

        // Load and add logo
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        logoImg.src = engagementLogo.url;
        await new Promise((resolve) => {
          logoImg.onload = resolve;
          setTimeout(resolve, 1000);
        });

        // Calculate logo dimensions (max height 25mm, maintain aspect ratio)
        const maxLogoHeight = 25;
        const maxLogoWidth = 80;
        let logoWidth = maxLogoWidth;
        let logoHeight = maxLogoHeight;
        if (logoImg.width && logoImg.height) {
          const ratio = logoImg.width / logoImg.height;
          if (ratio > maxLogoWidth / maxLogoHeight) {
            logoWidth = maxLogoWidth;
            logoHeight = maxLogoWidth / ratio;
          } else {
            logoHeight = maxLogoHeight;
            logoWidth = maxLogoHeight * ratio;
          }
        }

        const logoX = (pageWidth - logoWidth) / 2;
        pdf.addImage(logoImg, 'PNG', logoX, 8, logoWidth, logoHeight);

        // Add "Engagement Letter" text below logo
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Engagement Letter', pageWidth / 2, headerHeight - 6, { align: 'center' });
      } catch (e) {
        console.warn('Could not add logo to PDF:', e);
        // Fallback to text header
        pdf.rect(0, 0, pageWidth, 45, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(22);
        pdf.setFont('helvetica', 'bold');
        pdf.text('LC Timber Frames', pageWidth / 2, 18, { align: 'center' });
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Engagement Letter', pageWidth / 2, 32, { align: 'center' });
        headerHeight = 45;
      }
    } else {
      // Text-only header
      pdf.rect(0, 0, pageWidth, 45, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.text('LC Timber Frames', pageWidth / 2, 18, { align: 'center' });
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Engagement Letter', pageWidth / 2, 32, { align: 'center' });
    }

    yPos = headerHeight + 10;

    // Title
    pdf.setTextColor(17, 24, 39);
    addText(letterData.title, 18, true);
    yPos += 5;

    // Client info
    addText(`Prepared for: ${letterData.clientName}`, 12);
    addText(letterData.clientEmail, 10, false, '#6b7280');
    yPos += 8;

    // Determine letter type
    const isRetainer = letterData.letterType === 'retainer' || letterData.designFee === 1600;

    if (isRetainer) {
      // Retainer letter format
      checkNewPage(40);
      addText('Introduction', 14, true, '#1a365d');
      addText(`Dear ${letterData.clientName.split(' ')[0]},`, 11);
      addText('At Leiper\'s Creek Timber Frames, we\'re passionate about translating bold ideas into timeless designs through close collaboration with our exceptional clients. This proposal outlines our tailored approach to the concept definition phase, including our flexible fee structure.', 10);
      yPos += 5;

      // Scope of Services
      checkNewPage(60);
      addText('Scope of Services', 14, true, '#1a365d');
      addText('Deliverables:', 11, true);
      const deliverables = [
        '1. Interactive 3D Model - Browser-based 3D model with feedback before 2D documentation',
        '2. 2D Draft Plans - Structure views and elevations, iterated until complete'
      ];
      deliverables.forEach(d => {
        checkNewPage(10);
        addText(d, 10);
      });
      yPos += 5;

      // Fee Structure
      checkNewPage(60);
      addText('Fee Structure', 14, true, '#1a365d');
      addText('Concept Design Fee: A one-time payment of $1,600 is required upon signing to begin design work on your project. 2D draft plans are included.', 10);
      yPos += 5;

      // Terms
      checkNewPage(80);
      addText('Terms & Conditions', 14, true, '#1a365d');
      RETAINER_TERMS_CONDITIONS.forEach(term => {
        checkNewPage(15);
        addText(`${term.title}: ${term.content}`, 9);
      });
    } else {
      // Concept letter format
      checkNewPage(40);
      addText('Description of Services', 14, true);
      addText('1. The purpose of this service is to visually and clearly define the timber frame which Leiper\'s Creek Timber Frames will price and provide.', 10);
      addText('2. One revision to this model is included at this rate.', 10);
      addText('3. Additional design services are available at our standard rates.', 10);
      yPos += 8;

      // Deliverables
      checkNewPage(40);
      addText('Deliverables', 14, true);
      addText('Upon completion, you will receive:', 10);
      letterData.deliverables.forEach(d => {
        checkNewPage(8);
        addText(`• ${d}`, 10);
      });
      yPos += 8;

      // Terms
      checkNewPage(60);
      addText('Terms and Conditions', 14, true);
      letterData.termsAndConditions.split('\n').forEach(para => {
        if (para.trim()) {
          checkNewPage(10);
          addText(para, 9);
        }
      });
    }

    // Design Fee
    checkNewPage(30);
    yPos += 5;
    pdf.setFillColor(240, 253, 244);
    pdf.rect(margin, yPos - 5, contentWidth, 25, 'F');
    pdf.setTextColor(22, 101, 52);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Design Fee', margin + 5, yPos + 3);
    pdf.setFontSize(24);
    pdf.text(`$${letterData.designFee.toLocaleString()}`, margin + 5, yPos + 15);
    yPos += 30;

    // Signature Section
    checkNewPage(60);
    yPos += 10;
    addText('Signature', 14, true);

    // Add signature image
    try {
      const img = new Image();
      img.src = signatureDataUrl;
      await new Promise((resolve) => {
        img.onload = resolve;
        setTimeout(resolve, 500); // Fallback timeout
      });
      pdf.addImage(signatureDataUrl, 'PNG', margin, yPos, 60, 25);
      yPos += 30;
    } catch (e) {
      console.error('Error adding signature to PDF:', e);
      yPos += 5;
    }

    addText(`Signed by: ${signedBy}`, 10);
    addText(`Date: ${signedAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} at ${signedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`, 10);

    // Footer
    const footerY = pdf.internal.pageSize.getHeight() - 15;
    pdf.setFontSize(9);
    pdf.setTextColor(107, 114, 128);
    pdf.text(`© ${new Date().getFullYear()} Leiper's Creek Timber Frames | info@lctimberframes.com`, pageWidth / 2, footerY, { align: 'center' });

    return pdf.output('blob');
  };

  // Handle signature — server callable writes signature + serverVerified (guest client updates denied).
  const handleSign = async (signatureData: { dataUrl: string; signedBy: string }) => {
    if (letter && isVoidedEngagementLetter(letter.status)) {
      alert('This agreement was voided and cannot be signed.');
      return;
    }
    if (!letterId || !letter) return;

    try {
      const signedAt = new Date();
      await signEngagementLetterServer({
        letterId,
        signedBy: signatureData.signedBy,
        dataUrl: signatureData.dataUrl,
      });

      // Generate and save PDF (only if employee has permission to write to storage)
      let signedPdfUrl: string | undefined;
      if (isStaffPortalUser) {
        try {
          const pdfBlob = await generateSignedPdf(
            letter,
            signatureData.dataUrl,
            signatureData.signedBy,
            signedAt
          );

          // Upload to Firebase Storage
          const pdfFileName = `engagement-letters/${letterId}/${letter.slug}-signed.pdf`;
          const storageRef = ref(storage, pdfFileName);
          await uploadBytes(storageRef, pdfBlob);
          signedPdfUrl = await getDownloadURL(storageRef);
          await updateDoc(doc(db, 'engagementLetters', letterId), {
            signedPdfUrl,
            updatedAt: Timestamp.now(),
          });
        } catch (pdfErr) {
          console.error('Error generating/uploading PDF:', pdfErr);
          // Continue with signature even if PDF fails
        }
      }

      setLetter(prev => prev ? {
        ...prev,
        signature: {
          dataUrl: signatureData.dataUrl,
          signedBy: signatureData.signedBy,
          signedAt,
        },
        status: 'signed',
        signedAt,
        signedPdfUrl,
      } : null);

      // Portal → 3d-model and deal → design advance run server-side in
      // onEngagementLetterSigned when serverVerified flips true.
      // Staff-only: fan out engagement-signed notifications (guest list of users is denied).
      if (isStaffPortalUser) {
        try {
          const usersRef = collection(db, 'users');
          const usersSnap = await getDocs(usersRef);
          const notifBatch = usersSnap.docs
            .filter((userDoc) => isDealWonNotifyRecipient(userDoc.data() || {}))
            .map(userDoc => {
              return addDoc(collection(db, 'userNotifications'), {
                userId: userDoc.id,
                type: 'deal_won',
                title: 'Engagement Letter Signed',
                message: `${letter.clientName} signed their engagement letter ($${letter.designFee.toLocaleString()})`,
                read: false,
                leadId: letter.leadId || null,
                dealId: letter.dealId || null,
                sourceId: letterId,
                portalStep: 'design-fee',
                isPriority: true,
                createdAt: Timestamp.now(),
              });
            });
          await Promise.allSettled(notifBatch);
        } catch (notifErr) {
          console.error('[Engagement] Failed to fan out signed notifications:', notifErr);
        }
      }

      alert('Thank you for signing! You can now proceed to payment.' + (signedPdfUrl ? ' A signed copy is available for download.' : ''));
    } catch (err) {
      console.error('Error saving signature:', err);
      alert('Failed to save signature. Please try again.');
    }
  };

  // Helper to safely parse dates from Firestore Timestamps or Date objects
  const parseFirestoreDate = (date: any): Date => {
    if (!date) return new Date();
    if (date.toDate && typeof date.toDate === 'function') {
      return date.toDate(); // Firestore Timestamp
    }
    if (date instanceof Date) {
      return date;
    }
    if (typeof date === 'string' || typeof date === 'number') {
      return new Date(date);
    }
    return new Date();
  };

  // Handle payment click
  const handlePaymentClick = (url: string) => {
    window.open(url, '_blank');
  };

  // Find the correct payment link based on letter type or fee amount
  const getPaymentLink = useMemo(() => {
    if (paymentLinks.length === 0 || !letter) return null;

    // 1. Try to match by explicit settings
    if (letter.letterType === 'retainer' && retainerPaymentLinkId) {
      const link = paymentLinks.find(l => l.id === retainerPaymentLinkId);
      if (link) return link;
    }

    if (letter.letterType === 'concept' && conceptPaymentLinkId) {
      const link = paymentLinks.find(l => l.id === conceptPaymentLinkId);
      if (link) return link;
    }

    // 2. Try to match by letter type (legacy/fallback)
    if (letter.letterType === 'retainer') {
      const retainerLink = paymentLinks.find(link =>
        link.name.toLowerCase().includes('retainer') ||
        link.name.toLowerCase().includes('design')
      );
      if (retainerLink) return retainerLink;
    }

    if (letter.letterType === 'concept') {
      const conceptLink = paymentLinks.find(link =>
        link.name.toLowerCase().includes('concept')
      );
      if (conceptLink) return conceptLink;
    }

    // Fallback: try to match by fee amount
    if (letter.designFee === 1600) {
      const retainerLink = paymentLinks.find(link =>
        link.name.toLowerCase().includes('retainer') ||
        link.name.toLowerCase().includes('design') ||
        link.name.includes('1600')
      );
      if (retainerLink) return retainerLink;
    }

    if (letter.designFee === 500) {
      const conceptLink = paymentLinks.find(link =>
        link.name.toLowerCase().includes('concept') ||
        link.name.includes('500')
      );
      if (conceptLink) return conceptLink;
    }

    // Final fallback: first available link
    return paymentLinks[0];
  }, [paymentLinks, letter, conceptPaymentLinkId, retainerPaymentLinkId]);

  // Check if this is a retainer letter (for different rendering)
  const isRetainerLetter = letter?.letterType === 'retainer' || letter?.designFee === 1600;

  // Loading state
  if (loading) {
    return (
      <div className="engagement-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading engagement letter...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="engagement-page">
        <div className="error-container">
          <h2>Unable to Load</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="engagement-page">
        <div className="pin-entry-container">
          <div className="pin-entry-card">
            <h1>Access Restricted</h1>
            <p>This engagement letter can only be viewed through your client portal. Please use the link provided to you to access your portal.</p>
          </div>
        </div>
      </div>
    );
  }

  // Main content
  if (!letter) return null;

  const isVoided = isVoidedEngagementLetter(letter.status);
  const isSigned = !isVoided && (letter.status === 'signed' || letter.status === 'paid');
  const isPaid = !isVoided && letter.status === 'paid';

  return (
    <div className="engagement-page">
      {/* Blurred Background */}
      {engagementBackground && (
        <div
          className="engagement-background"
          style={{ backgroundImage: `url(${engagementBackground.url})` }}
        />
      )}

      <div className="engagement-container">
        {/* Back to Portal */}
        {cameFromPortal && (
          <button
            className="engagement-back-btn"
            onClick={() => navigate(`/portal/${portalSlug}`)}
          >
            <ArrowLeft size={16} />
            Back to Portal
          </button>
        )}

        {isVoided && (
          <section className="voided-section" role="status">
            <div className="voided-badge">
              <h3>This agreement was voided</h3>
            </div>
            <p>
              This engagement letter is no longer active and cannot be signed or paid.
              Please contact Leiper&apos;s Creek Timber Frames if you need a new agreement.
            </p>
          </section>
        )}

        {/* Header */}
        <header className="engagement-header">
          <div className="brand-logo">
            {engagementLogo ? (
              <img src={engagementLogo.url} alt="Company Logo" className="company-logo" />
            ) : (
              <h1>LC Timber Frames</h1>
            )}
          </div>
          <h2 className="engagement-title">Engagement Letter</h2>
        </header>

        {/* Retainer Letter Format */}
        {isRetainerLetter ? (
          <>
            {/* Introduction Section */}
            <section className="content-section retainer-intro">
              <h2 className="section-title" style={{ marginLeft: 0 }}>Introduction</h2>
              <p className="client-greeting">Dear {letter.clientName.split(' ')[0]},</p>
              <p>
                At Leiper's Creek Timber Frames, we're passionate about translating bold ideas into timeless designs through close
                collaboration with our exceptional clients. This proposal outlines our tailored approach to the concept definition
                phase, including our flexible fee structure.
              </p>
              <p>We're excited to move forward and deliver a foundation that exceeds your expectations!</p>

              {/* Video moved to portal splash page
              {engagementVideoUrl && (
                <div className="video-embed-container">
                  <div className="video-embed-wrapper">
                    <iframe
                      src={engagementVideoUrl.includes('/embed/') ? engagementVideoUrl : engagementVideoUrl.replace('watch?v=', 'embed/')}
                      title="Learn About Our Timber Framing Process"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}
              */}
              <p className="closing-intro">We look forward to meeting with you to discuss next steps.</p>
            </section>

            {/* Scope of Services Section (Collapsible, Closed Default) */}
            <CollapsibleSection title="Scope of Services" defaultOpen={false} className="scope-section">
              <h3>Deliverables</h3>
              <ol className="deliverables-list numbered">
                <li><strong>Interactive 3D Model</strong> - Your first submission will be a browser-based 3D model. We'll request your feedback before starting 2D documentation.</li>
                <li><strong>2D Draft Plans</strong> - Structure views and elevations. We'll iterate until we've completely captured your vision.</li>
              </ol>

              {/* 3D Model Preview */}
              {engagement3DModel && (
                <div className="deliverable-preview">
                  <h4>Sample 3D Model</h4>
                  <div className="model-container">
                    <iframe
                      title="3D Model Preview"
                      src={engagement3DModel.url}
                      frameBorder="0"
                      allow="autoplay; fullscreen; xr-spatial-tracking; accelerometer; gyroscope; magnetometer"
                      allowFullScreen
                      style={{ display: 'block' }}
                    />
                  </div>
                </div>
              )}

              {/* Sample Plans Image */}
              {samplePlansImage && (
                <div className="deliverable-preview">
                  <h4>Sample Plans</h4>
                  <img
                    src={samplePlansImage.url}
                    alt="Sample Plans"
                    className="sample-plans-image"
                  />
                </div>
              )}

              <p className="scope-note">
                This phase is designed for flexibility, allowing us to evolve ideas without rigid constraints—ideal for high-end
                projects where vision takes time to crystallize. Estimated timeline: 2-4 weeks, subject to our mutual availability.
              </p>
            </CollapsibleSection>

            {/* Fee Structure Section (Collapsible, Closed Default) */}
            <CollapsibleSection title="Fee Structure" defaultOpen={false} className="fee-structure-section">
              <div className="fee-intro">
                <strong>Concept Design Fee:</strong> A one-time payment of $1,600 is required upon signing to begin design work on your project.
                2D draft plans are included. This secures your spot in our schedule and allows us to immediately start bringing your vision to life.
              </div>
              <div className="fee-details">
                <p><strong>What's Included</strong>: Interactive 3D model, 2D draft plans with structure views and elevations.</p>
                <p><strong>Expenses</strong>: Reimbursable at cost (e.g., travel, printing &gt;$100); pre-approved.</p>
              </div>
            </CollapsibleSection>
          </>
        ) : (
          <>
            {/* Standard Concept Letter Format */}
            {/* Client Info */}
            <section className="client-section">
              <h2>{letter.title}</h2>
              <p className="client-name">Prepared for: <strong>{letter.clientName}</strong></p>
              <p className="client-email">{letter.clientEmail}</p>
            </section>

            {/* Description of Services */}
            <section className="content-section">
              <h3>Description of Services</h3>
              <ol className="services-list">
                <li>The purpose of this service is to visually and clearly define the timber frame which Leiper's Creek Timber Frames will price and provide.</li>
                <li>One revision to this model is included at this rate.</li>
                <li>Additional design services are available including foundation/foundation engineering/wall details/basic elevations at our standard rates. Contact your sales representative for additional services.</li>
              </ol>
            </section>

            {/* 3D Model Preview */}
            {engagement3DModel && (
              <section className="content-section model-section">
                <h3>3D Model Preview</h3>
                <div className="model-container">
                  <iframe
                    title="3D Model"
                    src={engagement3DModel.url}
                    frameBorder="0"
                    allow="autoplay; fullscreen; xr-spatial-tracking; accelerometer; gyroscope; magnetometer"
                    allowFullScreen
                    style={{ display: 'block' }}
                  />
                </div>
              </section>
            )}

            {/* Deliverables */}
            <section className="content-section">
              <h3>Deliverables</h3>
              <p className="deliverables-intro">
                Upon completion, you will receive:
              </p>
              <ul className="deliverables-list">
                {letter.deliverables.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            {/* Fee */}
            <section className="fee-section">
              <h3>Design Fee</h3>
              <div className="fee-amount">
                ${letter.designFee.toLocaleString()}
              </div>
              <p className="fee-description">
                One-time design fee for 3D timber frame modeling services
              </p>
            </section>

            {/* Terms */}
            <section className="content-section">
              <h3>Terms and Conditions</h3>
              <div className="terms-content">
                {letter.termsAndConditions.split('\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Signature Section */}
        {!isVoided && !isPaid && (
          <section className="signature-section">
            {isRetainerLetter ? (
              <>
                {/* Single Signature for Retainer (Client Only) */}
                <div className="dual-signature-container" style={{ gridTemplateColumns: '1fr', maxWidth: '100%', margin: '0 auto' }}>
                  {/* Client Signature */}
                  <div className="signature-column client">
                    {isSigned && letter.signature ? (
                      <>
                        <div className="signature-display">
                          <img src={letter.signature.dataUrl} alt="Signature" />
                        </div>
                        <p className="signer-name">{letter.signature.signedBy}</p>
                        <p className="signer-company">{letter.clientName}</p>
                        <p className="signature-date">
                          Signed: {parseFirestoreDate(letter.signature.signedAt).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </p>
                      </>
                    ) : isStakeholderViewer ? (
                      <p className="signature-instructions" style={{ margin: 0 }}>
                        View only — the client signs this agreement through their portal.
                      </p>
                    ) : (
                      <SignaturePad
                        onSign={handleSign}
                        initialName={letter.clientName}
                      />
                    )}
                  </div>
                </div>
                {isSigned && (
                  <div className="signature-checkmark">✓ Agreement Signed</div>
                )}
              </>
            ) : (
              <>
                {/* Standard Single Signature */}
                <h3>Signature</h3>
                {isSigned && letter.signature ? (
                  <div className="signature-complete">
                    <div className="signature-display">
                      <img src={letter.signature.dataUrl} alt="Signature" />
                    </div>
                    <p className="signature-info">
                      Signed by <strong>{letter.signature.signedBy}</strong> on{' '}
                      {parseFirestoreDate(letter.signature.signedAt).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </p>
                    <div className="signature-checkmark">✓ Agreement Signed</div>
                  </div>
                ) : isStakeholderViewer ? (
                  <p className="signature-instructions" style={{ margin: 0 }}>
                    View only — the client signs this agreement through their portal.
                  </p>
                ) : (
                  <SignaturePad
                    onSign={handleSign}
                    initialName={letter.clientName}
                  />
                )}
              </>
            )}
          </section>
        )}

        {/* Testimonials & Terms - Show BELOW signature section for retainer letters before signing */}
        {isRetainerLetter && !isVoided && !isPaid && !isSigned && (
          <>
            <CollapsibleSection title="What Our Clients Say" defaultOpen={false} className="testimonials-section">
              <div className="testimonials">
                <blockquote className="testimonial">
                  <p>
                    "Matt and his team are true artisans and a pleasure to work with. Matt's professionalism through the whole
                    process (concept, design, engineer, build) was very easy to follow with no issues. They out performed all
                    expectations we had, and the craftsmanship of our build is 10/10. Can't say enough about how great the experience was!"
                  </p>
                  <cite>Brandon Potter - Cynthiana, KY</cite>
                </blockquote>
                <blockquote className="testimonial">
                  <p>
                    "If I could give ten stars, I would. Everyone who sees my new workshop is blown away by the attention to detail
                    and craftsmanship. Matt shepherded this project throughout the entire process and every participating
                    subcontractor has remarked on their pleasure at seeing a true Post & Beam build done right."
                  </p>
                  <cite>Robert McCrary - Franklin, TN</cite>
                </blockquote>
                <blockquote className="testimonial">
                  <p>
                    "We had an excellent experience with Leiper's Creek Timber Frames. The craftsmanship was top-notch and the
                    attention to detail throughout the project was outstanding. Matt, the owner, was great to work with—professional,
                    responsive, and clearly passionate about the quality of his work."
                  </p>
                  <cite>Joe Crider, Legend Homes - Franklin, TN</cite>
                </blockquote>
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Terms & Conditions" defaultOpen={false} className="terms-section">
              <ul className="terms-list">
                {RETAINER_TERMS_CONDITIONS.map((term, index) => (
                  <li key={index}>
                    <strong>{term.title}</strong>: {term.content}
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
          </>
        )}

        {/* Payment Section */}
        {!isVoided && isSigned && !isPaid && (
          <section className="payment-section">
            <h3>Complete Payment</h3>
            <p>
              Thank you for signing! To finalize your engagement, please complete the payment below.
            </p>
            <div className="payment-buttons">
              {getPaymentLink ? (
                <button
                  className="payment-btn"
                  onClick={() => handlePaymentClick(getPaymentLink.url)}
                >
                  Proceed to Payment
                </button>
              ) : (
                <p className="no-payment-message">
                  Payment link not configured. Please contact us to arrange payment.
                </p>
              )}
              {/* Download Signed PDF */}
              {letter.signedPdfUrl ? (
                <a
                  href={letter.signedPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-pdf-btn"
                  download
                >
                  <Download size={18} />
                  Download Signed Agreement
                </a>
              ) : letter.signature && (
                <button
                  className="download-pdf-btn"
                  onClick={async () => {
                    if (!letter.signature || !letterId) return;
                    try {
                      const pdfBlob = await generateSignedPdf(
                        letter,
                        letter.signature.dataUrl,
                        letter.signature.signedBy,
                        parseFirestoreDate(letter.signature.signedAt)
                      );

                      // If employee, try to save to Firebase
                      if (isStaffPortalUser) {
                        try {
                          const pdfFileName = `engagement-letters/${letterId}/${letter.slug}-signed.pdf`;
                          const storageRef = ref(storage, pdfFileName);
                          await uploadBytes(storageRef, pdfBlob);
                          const signedPdfUrl = await getDownloadURL(storageRef);

                          await updateDoc(doc(db, 'engagementLetters', letterId), {
                            signedPdfUrl,
                            updatedAt: Timestamp.now(),
                          });

                          setLetter(prev => prev ? { ...prev, signedPdfUrl } : null);
                        } catch (uploadErr) {
                          console.warn('Could not save PDF to storage:', uploadErr);
                        }
                      }

                      // Download PDF directly
                      const url = URL.createObjectURL(pdfBlob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `${letter.slug}-signed.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(url);
                    } catch (err) {
                      console.error('Error generating PDF:', err);
                      alert('Failed to generate PDF. Please try again.');
                    }
                  }}
                >
                  <Download size={18} />
                  Download Signed Agreement
                </button>
              )}
            </div>
          </section>
        )}

        {/* Paid Confirmation */}
        {isPaid && (
          <section className="paid-section">
            <div className="paid-badge">
              <span className="paid-icon">✓</span>
              <h3>Payment Complete</h3>
            </div>
            <p>
              Thank you! Your design engagement has been confirmed.
              We will begin work on your 3D timber frame model and
              will be in touch with updates soon.
            </p>
            {letter.signature && (
              <div className="signature-display small">
                <img src={letter.signature.dataUrl} alt="Signature" />
                <span>Signed by {letter.signature.signedBy}</span>
              </div>
            )}
            {/* Download Signed PDF */}
            {letter.signedPdfUrl ? (
              <a
                href={letter.signedPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="download-pdf-btn paid"
                download
              >
                <Download size={18} />
                Download Signed Agreement
              </a>
            ) : letter.signature && (
              <button
                className="download-pdf-btn paid"
                onClick={async () => {
                  if (!letter.signature || !letterId) return;
                  try {
                    const pdfBlob = await generateSignedPdf(
                      letter,
                      letter.signature.dataUrl,
                      letter.signature.signedBy,
                      parseFirestoreDate(letter.signature.signedAt)
                    );

                    // If employee, try to save to Firebase
                    if (isStaffPortalUser) {
                      try {
                        const pdfFileName = `engagement-letters/${letterId}/${letter.slug}-signed.pdf`;
                        const storageRef = ref(storage, pdfFileName);
                        await uploadBytes(storageRef, pdfBlob);
                        const signedPdfUrl = await getDownloadURL(storageRef);

                        await updateDoc(doc(db, 'engagementLetters', letterId), {
                          signedPdfUrl,
                          updatedAt: Timestamp.now(),
                        });

                        setLetter(prev => prev ? { ...prev, signedPdfUrl } : null);
                      } catch (uploadErr) {
                        console.warn('Could not save PDF to storage:', uploadErr);
                      }
                    }

                    // Download PDF directly
                    const url = URL.createObjectURL(pdfBlob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${letter.slug}-signed.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error('Error generating PDF:', err);
                    alert('Failed to generate PDF. Please try again.');
                  }
                }}
              >
                <Download size={18} />
                Download Signed Agreement
              </button>
            )}
          </section>
        )}

        {/* Testimonials Section - After Payment for Retainer Letters */}
        {isRetainerLetter && isSigned && (
          <CollapsibleSection title="What Our Clients Say" defaultOpen={false} className="testimonials-section">
            <div className="testimonials">
              <blockquote className="testimonial">
                <p>
                  "Matt and his team are true artisans and a pleasure to work with. Matt's professionalism through the whole
                  process (concept, design, engineer, build) was very easy to follow with no issues. They out performed all
                  expectations we had, and the craftsmanship of our build is 10/10. Can't say enough about how great the experience was!"
                </p>
                <cite>Brandon Potter - Cynthiana, KY</cite>
              </blockquote>
              <blockquote className="testimonial">
                <p>
                  "If I could give ten stars, I would. Everyone who sees my new workshop is blown away by the attention to detail
                  and craftsmanship. Matt shepherded this project throughout the entire process and every participating
                  subcontractor has remarked on their pleasure at seeing a true Post & Beam build done right."
                </p>
                <cite>Robert McCrary - Franklin, TN</cite>
              </blockquote>
              <blockquote className="testimonial">
                <p>
                  "We had an excellent experience with Leiper's Creek Timber Frames. The craftsmanship was top-notch and the
                  attention to detail throughout the project was outstanding. Matt, the owner, was great to work with—professional,
                  responsive, and clearly passionate about the quality of his work."
                </p>
                <cite>Joe Crider, Legend Homes - Franklin, TN</cite>
              </blockquote>
            </div>
          </CollapsibleSection>
        )}

        {/* Terms Section - After Testimonials for Retainer Letters */}
        {isRetainerLetter && isSigned && (
          <CollapsibleSection title="Terms & Conditions" defaultOpen={false} className="terms-section">
            <ul className="terms-list">
              {RETAINER_TERMS_CONDITIONS.map((term, index) => (
                <li key={index}>
                  <strong>{term.title}</strong>: {term.content}
                </li>
              ))}
            </ul>
          </CollapsibleSection>
        )}


        {/* Footer */}
        <footer className="engagement-footer">
          <p>
            Questions? Contact us at{' '}
            <a href="mailto:info@lctimberframes.com">info@lctimberframes.com</a>
          </p>
          <p className="copyright">© {new Date().getFullYear()} LC Timber Frames</p>
        </footer>
      </div>
    </div>
  );
};

export default EngagementPage;
